import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Download, CheckCircle, DollarSign, Edit2, Save, X, Plus, Trash2, MinusCircle, Sparkles } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import {
  ReadOnlyField,
  OwnerOperatorReviewSection,
  ApproveWithComplianceBlock,
  CompanyDriverReviewSection,
} from '../components/DriverReviewSections';
import { ApplicationAdminBar } from '../components/ApplicationAdminTools';
import { DriverPeriodDeductions } from '../components/DriverPeriodDeductions';
import { FuelReceiptsReview } from '../components/FuelReceiptsReview';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Permanent (recurring) deduction set — applied every payroll automatically.
const DEDUCTION_TYPES = [
  { value: 'eld', label: 'ELD' },
  { value: 'parking', label: 'Parking' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'lease_on', label: 'Lease-on' },
  { value: 'trailer_rental', label: 'Trailer Rental' },
  { value: 'dispatch_fee', label: 'Dispatch Fee' },
  { value: 'factoring_fee', label: 'Factoring Fee' },
];

// Legacy labels retained so existing data renders correctly
const LEGACY_DEDUCTION_LABELS = {
  truck_lease: 'Truck Lease Fee', fuel: 'Fuel Expense', other: 'Other Deduction',
  monthly_parking: 'Monthly Parking',
  daily_parking: 'Daily Parking',
  eld_fee: 'ELD Fee',
  toll: 'Toll',
  truck_wash: 'Truck Wash',
  repair: 'Repair',
  lease_on_fee: 'Lease-on Fee',
  trailer_fee: 'Trailer Fee',
};

const DEDUCTION_LABEL = { ...LEGACY_DEDUCTION_LABELS, ...Object.fromEntries(DEDUCTION_TYPES.map(d => [d.value, d.label])) };

const STATUS_COLORS = {
  invited: 'bg-slate-100 text-slate-700',
  registered: 'bg-blue-100 text-blue-700',
  pending_approval: 'bg-orange-100 text-orange-700',
  active: 'bg-green-100 text-green-700',
};

const STATUS_LABELS = {
  invited: 'Invited',
  registered: 'Registered',
  pending_approval: 'Pending Approval',
  active: 'Active',
};




const DriverDetail = () => {
  const { driverId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const canManageDeductions = ['company_admin', 'super_admin'].includes(authUser?.role);
  const [fuelRefreshKey, setFuelRefreshKey] = useState(0);
  const [driver, setDriver] = useState(null);
  const [profile, setProfile] = useState(null);
  const [pay, setPay] = useState(null);
  const [deductions, setDeductions] = useState([]);
  const [showDeductionForm, setShowDeductionForm] = useState(false);
  const [deductionForm, setDeductionForm] = useState({ deduction_type: 'eld', amount: '', is_percentage: false, is_active: true, notes: '' });
  const [deductionError, setDeductionError] = useState('');
  const [editingDeductionId, setEditingDeductionId] = useState(null);
  const [editingPay, setEditingPay] = useState(false);
  const [payForm, setPayForm] = useState({ pay_type: 'salary', pay_rate: '', effective_date: '', notes: '' });
  const [onboardingPay, setOnboardingPay] = useState(null);
  const [payError, setPayError] = useState('');
  const [savingPay, setSavingPay] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDriver();
  }, [driverId]);

  const fetchDriver = async () => {
    try {
      const [driverRes, payRes, dedRes] = await Promise.all([
        axios.get(`${API}/drivers/${driverId}`, { withCredentials: true }),
        axios.get(`${API}/drivers/${driverId}/pay`, { withCredentials: true }),
        axios.get(`${API}/drivers/${driverId}/deductions`, { withCredentials: true }),
      ]);
      setDriver(driverRes.data.driver);
      setProfile(driverRes.data.profile);
      setPay(payRes.data);
      setDeductions(dedRes.data);
      if (payRes.data) {
        setPayForm({
          pay_type: payRes.data.pay_type,
          pay_rate: String(payRes.data.pay_rate),
          effective_date: payRes.data.effective_date || '',
          notes: payRes.data.notes || '',
        });
      } else if (driverRes.data.onboarding_pay?.pay_type) {
        const ob = driverRes.data.onboarding_pay;
        setOnboardingPay(ob);
        setPayForm((f) => ({ ...f, pay_type: ob.pay_type, pay_rate: ob.pay_rate != null ? String(ob.pay_rate) : '' }));
      }
      if (searchParams.get('setup') === 'pay') {
        setEditingPay(true);
        setTimeout(() => document.querySelector('[data-testid="pay-structure-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to load driver');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDeduction = async () => {
    setDeductionError('');
    const amount = parseFloat(deductionForm.amount);
    if (isNaN(amount) || amount < 0) {
      setDeductionError('Amount must be a non-negative number');
      return;
    }
    if (deductionForm.is_percentage && amount > 100) {
      setDeductionError('Percentage must be 0-100');
      return;
    }
    try {
      const body = {
        deduction_type: deductionForm.deduction_type,
        amount,
        is_percentage: deductionForm.is_percentage,
        is_active: deductionForm.is_active,
        notes: deductionForm.notes || null,
      };
      if (editingDeductionId) {
        await axios.put(`${API}/drivers/${driverId}/deductions/${editingDeductionId}`, body, { withCredentials: true });
      } else {
        await axios.post(`${API}/drivers/${driverId}/deductions`, body, { withCredentials: true });
      }
      setShowDeductionForm(false);
      setEditingDeductionId(null);
      setDeductionForm({ deduction_type: 'eld', amount: '', is_percentage: false, is_active: true, notes: '' });
      fetchDriver();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setDeductionError(typeof detail === 'string' ? detail : 'Failed to save deduction');
    }
  };

  const handleEditDeduction = (d) => {
    setEditingDeductionId(d.id);
    setDeductionForm({
      deduction_type: d.deduction_type,
      amount: String(d.amount),
      is_percentage: d.is_percentage,
      is_active: d.is_active,
      notes: d.notes || '',
    });
    setShowDeductionForm(true);
    setDeductionError('');
  };

  const handleDeleteDeduction = async (deductionId) => {
    if (!window.confirm('Delete this deduction?')) return;
    try {
      await axios.delete(`${API}/drivers/${driverId}/deductions/${deductionId}`, { withCredentials: true });
      fetchDriver();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  const handleSavePay = async () => {
    setPayError('');
    const rate = parseFloat(payForm.pay_rate);
    if (isNaN(rate) || rate < 0) {
      setPayError('Pay rate must be a non-negative number');
      return;
    }
    setSavingPay(true);
    try {
      const res = await axios.put(
        `${API}/drivers/${driverId}/pay`,
        {
          pay_type: payForm.pay_type,
          pay_rate: rate,
          effective_date: payForm.effective_date || null,
          notes: payForm.notes || null,
        },
        { withCredentials: true }
      );
      setPay(res.data);
      setEditingPay(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPayError(typeof detail === 'string' ? detail : 'Failed to save pay');
    } finally {
      setSavingPay(false);
    }
  };

  const handleApprove = async () => {
    if (!window.confirm('Approve this driver?')) return;
    try {
      await axios.post(`${API}/drivers/${driverId}/approve`, {}, { withCredentials: true });
      fetchDriver();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Failed to approve');
    }
  };

  const handleDownloadDocument = async (docType) => {
    try {
      const res = await axios.get(
        `${API}/drivers/${driverId}/documents/${docType}`,
        { withCredentials: true }
      );
      const { file_name, file_data, content_type } = res.data;
      const byteCharacters = atob(file_data);
      const byteArrays = [];
      for (let i = 0; i < byteCharacters.length; i++) {
        byteArrays.push(byteCharacters.charCodeAt(i));
      }
      const blob = new Blob([new Uint8Array(byteArrays)], { type: content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download document');
    }
  };

  if (loading) {
    return <DashboardLayout><div className="p-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div></DashboardLayout>;
  }

  if (error || !driver) {
    return (
      <DashboardLayout>
        <div className="p-8">
          <p className="text-red-600">{error || 'Driver not found'}</p>
        </div>
      </DashboardLayout>
    );
  }

  const Field = ({ label, value }) => (
    <div>
      <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</p>
      <p className="text-sm text-slate-900 font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{value || '-'}</p>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="p-8 max-w-5xl mx-auto">
        <button
          data-testid="back-to-drivers-button"
          onClick={() => navigate('/drivers')}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
          Back to Drivers
        </button>

        <div className="bg-white border border-slate-200 rounded-md p-8 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 data-testid="driver-name-heading" className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>{driver.name}</h1>
              <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{driver.email}</p>
            </div>
            <div className="flex items-center gap-3">
              <span data-testid="driver-detail-status" className={`px-3 py-1 rounded text-xs font-semibold ${STATUS_COLORS[driver.status] || 'bg-slate-100'}`}>
                {STATUS_LABELS[driver.status] || driver.status}
              </span>
              {driver.status === 'pending_approval' && (
                <ApproveWithComplianceBlock
                  driverId={driver.id}
                  onApprove={handleApprove}
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <Field label="Driver Type" value={driver.driver_type ? (driver.driver_type === 'company_driver' ? 'Company Driver' : 'Owner Operator') : '-'} />
            <Field label="Registered At" value={driver.registered_at ? new Date(driver.registered_at).toLocaleDateString() : '-'} />
          </div>

          {canManageDeductions && driver?.status !== 'registered' && (
            <DriverPeriodDeductions driverId={driverId} driverName={driver.name} refreshKey={fuelRefreshKey} />
          )}
          {['company_admin', 'super_admin', 'dispatcher'].includes(authUser?.role) && driver?.status !== 'registered' && (
            <FuelReceiptsReview driverId={driverId} driverName={driver.name} role={authUser?.role} onChanged={() => setFuelRefreshKey((k) => k + 1)} />
          )}

          {profile ? (
            <>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900 mb-6 pt-4 border-t border-slate-200" style={{ fontFamily: 'Work Sans, sans-serif' }}>Application Details</h2>
              <ApplicationAdminBar driverId={driverId} profile={profile} onChanged={fetchDriver} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Field label="Full Name" value={profile.full_name} />
                <Field label="Date of Birth" value={profile.date_of_birth} />
                <Field label="Phone" value={profile.phone} />
                <Field label="SSN (Last 4)" value={profile.ssn_last_4 ? `***-**-${profile.ssn_last_4}` : '-'} />
                <Field label="US Work Authorization" value={profile.us_work_authorization ? 'Yes' : 'No'} />
              </div>

              <h2 className="text-xl font-semibold tracking-tight text-slate-900 mb-6 pt-4 border-t border-slate-200" style={{ fontFamily: 'Work Sans, sans-serif' }}>CDL Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <Field label="CDL Number" value={profile.cdl_number} />
                <Field label="CDL Class" value={profile.cdl_class ? `Class ${profile.cdl_class}` : '-'} />
                <Field label="CDL Expiration" value={profile.cdl_expiration} />
              </div>

              <h2 className="text-xl font-semibold tracking-tight text-slate-900 mb-6 pt-4 border-t border-slate-200" style={{ fontFamily: 'Work Sans, sans-serif' }}>Documents</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'cdl_license', label: 'CDL License', testid: 'cdl-document-card', btnTestid: 'download-cdl-button' },
                  { key: 'medical_card', label: 'Medical Card', testid: 'medical-document-card', btnTestid: 'download-medical-button' },
                  ...(profile.driver_type === 'owner_operator' ? [
                    { key: 'truck_registration', label: 'Truck Registration', testid: 'truck-reg-document-card', btnTestid: 'download-truck-reg-button' },
                    { key: 'physical_damage_insurance', label: 'Physical Damage Insurance', testid: 'physical-damage-document-card', btnTestid: 'download-physical-damage-button' },
                    { key: 'w9_form', label: 'IRS Form W-9', testid: 'w9-document-card', btnTestid: 'download-w9-button' },
                  ] : []),
                ].map(({ key, label, testid, btnTestid }) => {
                  const doc = profile.documents?.[key];
                  return (
                    <div key={key} data-testid={testid} className="border border-slate-200 rounded-md p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-[#002FA7]" />
                        <div>
                          <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</p>
                          <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{doc?.file_name || 'Not uploaded'}</p>
                        </div>
                      </div>
                      {doc?.has_file && (
                        <button
                          data-testid={btnTestid}
                          onClick={() => handleDownloadDocument(key)}
                          className="text-[#002FA7] hover:text-[#00247D]"
                          title={`Download ${label}`}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {profile.driver_type === 'owner_operator' && (
                <OwnerOperatorReviewSection profile={profile} />
              )}
              {profile.driver_type === 'company_driver' && (
                <CompanyDriverReviewSection profile={profile} />
              )}
            </>
          ) : (
            <p className="text-sm text-slate-500 italic" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Driver has not submitted their application yet.</p>
          )}
        </div>

        <div data-testid="pay-structure-section" className="bg-white border border-slate-200 rounded-md p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <DollarSign className="w-6 h-6 text-[#34C759]" strokeWidth={2} />
              <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Pay Structure</h2>
            </div>
            {!editingPay && (
              <button
                data-testid="edit-pay-button"
                onClick={() => { setEditingPay(true); setPayError(''); }}
                className="flex items-center gap-2 text-sm text-[#002FA7] hover:text-[#00247D] font-semibold"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <Edit2 className="w-4 h-4" />
                {pay ? 'Edit' : 'Set Pay'}
              </button>
            )}
          </div>

          {payError && (
            <div data-testid="pay-error-message" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">{payError}</div>
          )}

          {editingPay ? (
            <div className="space-y-4">
              {!pay && onboardingPay && (
                <div data-testid="pay-prefill-hint" className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <Sparkles className="w-4 h-4 text-[#002FA7] flex-shrink-0" />
                  Pre-filled from the driver&apos;s onboarding profile ({onboardingPay.pay_type.replace('_', ' ')}{onboardingPay.pay_rate != null ? ` · ${onboardingPay.pay_type === 'percentage' ? `${onboardingPay.pay_rate}%` : `$${onboardingPay.pay_rate}`}` : ''}). Review and click Save to activate it for payroll.
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Pay Type</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'salary', label: 'Salary', hint: '$/year' },
                    { value: 'per_mile', label: 'Per Mile', hint: '$/mile' },
                    { value: 'hourly', label: 'Hourly', hint: '$/hour' },
                    { value: 'percentage', label: 'Percentage', hint: '% of load' },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      data-testid={`pay-type-${opt.value}`}
                      className={`flex flex-col gap-1 p-3 border rounded-md cursor-pointer transition-all ${
                        payForm.pay_type === opt.value ? 'border-[#002FA7] bg-blue-50' : 'border-slate-300 bg-white hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="pay_type"
                          value={opt.value}
                          checked={payForm.pay_type === opt.value}
                          onChange={(e) => setPayForm({ ...payForm, pay_type: e.target.value })}
                        />
                        <span className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{opt.label}</span>
                      </div>
                      <span className="text-xs text-slate-500 ml-5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{opt.hint}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {payForm.pay_type === 'percentage' ? 'Rate (%)' : 'Rate ($)'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    data-testid="pay-rate-input"
                    value={payForm.pay_rate}
                    onChange={(e) => setPayForm({ ...payForm, pay_rate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    placeholder={payForm.pay_type === 'salary' ? '65000' : payForm.pay_type === 'percentage' ? '25' : '0.65'}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Effective Date (optional)</label>
                  <input
                    type="date"
                    data-testid="pay-effective-date-input"
                    value={payForm.effective_date}
                    onChange={(e) => setPayForm({ ...payForm, effective_date: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Notes (optional)</label>
                <input
                  type="text"
                  data-testid="pay-notes-input"
                  value={payForm.notes}
                  onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button
                  data-testid="save-pay-button"
                  onClick={handleSavePay}
                  disabled={savingPay}
                  className="flex items-center gap-2 bg-[#002FA7] text-white px-5 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200 disabled:opacity-50"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  <Save className="w-4 h-4" />
                  {savingPay ? 'Saving...' : 'Save Pay Structure'}
                </button>
                <button
                  data-testid="cancel-pay-button"
                  onClick={() => { setEditingPay(false); setPayError(''); }}
                  className="flex items-center gap-2 text-slate-700 px-5 py-2 rounded-md font-semibold hover:bg-slate-100 transition-all"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
              </div>
            </div>
          ) : pay ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Pay Type</p>
                <p data-testid="pay-type-display" className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  {{
                    salary: 'Salary',
                    per_mile: 'Per Mile',
                    hourly: 'Hourly',
                    percentage: 'Percentage',
                  }[pay.pay_type]}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Rate</p>
                <p data-testid="pay-rate-display" className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  {pay.pay_type === 'salary' && `$${pay.pay_rate.toLocaleString()}/yr`}
                  {pay.pay_type === 'per_mile' && `$${pay.pay_rate.toFixed(2)}/mi`}
                  {pay.pay_type === 'hourly' && `$${pay.pay_rate.toFixed(2)}/hr`}
                  {pay.pay_type === 'percentage' && `${pay.pay_rate}%`}
                </p>
              </div>
              {pay.effective_date && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Effective</p>
                  <p className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{pay.effective_date}</p>
                </div>
              )}
              {pay.notes && (
                <div className="md:col-span-3 pt-3 border-t border-slate-200">
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Notes</p>
                  <p className="text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{pay.notes}</p>
                </div>
              )}
            </div>
          ) : (
            <p data-testid="pay-not-set-message" className="text-sm text-slate-500 italic" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No pay structure set. Click "Set Pay" to configure.</p>
          )}
        </div>

        <div data-testid="deductions-section" className="bg-white border border-slate-200 rounded-md p-8 mt-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <MinusCircle className="w-6 h-6 text-[#FF3B30]" strokeWidth={2} />
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Recurring Deductions</h2>
                <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Auto-applied each payroll run</p>
              </div>
            </div>
            {!showDeductionForm && (
              <button
                data-testid="add-deduction-button"
                onClick={() => { setShowDeductionForm(true); setEditingDeductionId(null); setDeductionForm({ deduction_type: 'eld', amount: '', is_percentage: false, is_active: true, notes: '' }); setDeductionError(''); }}
                className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <Plus className="w-4 h-4" />
                Add Deduction
              </button>
            )}
          </div>

          {deductionError && (
            <div data-testid="deduction-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">{deductionError}</div>
          )}

          {showDeductionForm && (
            <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Type</label>
                  <select
                    data-testid="deduction-type-select"
                    value={deductionForm.deduction_type}
                    onChange={(e) => setDeductionForm({ ...deductionForm, deduction_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none bg-white"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    {DEDUCTION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{deductionForm.is_percentage ? 'Rate (%)' : 'Amount ($)'}</label>
                  <input
                    type="number"
                    step="0.01"
                    data-testid="deduction-amount-input"
                    value={deductionForm.amount}
                    onChange={(e) => setDeductionForm({ ...deductionForm, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    required
                  />
                </div>
                <div className="md:col-span-2 flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    <input
                      type="checkbox"
                      data-testid="deduction-is-percentage-checkbox"
                      checked={deductionForm.is_percentage}
                      onChange={(e) => setDeductionForm({ ...deductionForm, is_percentage: e.target.checked })}
                    />
                    <span className="text-sm text-slate-700">Percentage of gross pay</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    <input
                      type="checkbox"
                      data-testid="deduction-is-active-checkbox"
                      checked={deductionForm.is_active}
                      onChange={(e) => setDeductionForm({ ...deductionForm, is_active: e.target.checked })}
                    />
                    <span className="text-sm text-slate-700">Active</span>
                  </label>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Notes (optional)</label>
                  <input
                    type="text"
                    data-testid="deduction-notes-input"
                    value={deductionForm.notes}
                    onChange={(e) => setDeductionForm({ ...deductionForm, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="save-deduction-button"
                  onClick={handleSaveDeduction}
                  className="bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  {editingDeductionId ? 'Update' : 'Save'}
                </button>
                <button
                  data-testid="cancel-deduction-button"
                  onClick={() => { setShowDeductionForm(false); setEditingDeductionId(null); setDeductionError(''); }}
                  className="text-slate-700 px-4 py-2 rounded-md font-semibold hover:bg-slate-100 transition-all"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {deductions.length === 0 ? (
            <p data-testid="no-deductions-message" className="text-sm text-slate-500 italic" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No recurring deductions configured.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Type</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Amount</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Status</th>
                    <th className="text-left py-3 px-2 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Notes</th>
                    <th className="py-3 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {deductions.map((d) => (
                    <tr key={d.id} data-testid={`deduction-row-${d.id}`} className="border-b border-slate-100">
                      <td className="py-3 px-2 text-sm text-slate-900 font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{DEDUCTION_LABEL[d.deduction_type] || d.deduction_type}</td>
                      <td className="py-3 px-2 text-sm text-slate-900 font-semibold" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{d.is_percentage ? `${d.amount}%` : `$${d.amount.toFixed(2)}`}</td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${d.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                          {d.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{d.notes || '-'}</td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-3">
                          <button data-testid={`edit-deduction-${d.id}`} onClick={() => handleEditDeduction(d)} className="text-[#002FA7] hover:text-[#00247D]">
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button data-testid={`delete-deduction-${d.id}`} onClick={() => handleDeleteDeduction(d.id)} className="text-red-600 hover:text-red-800">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DriverDetail;
